import './services/firebaseApp'

import React from 'react'
import { StyleProvider, I18NProvider, useIsomorphicEffect } from '@codeleap/common'
import { Settings, variantProvider, I18N, LocalStorage } from './app'
import {
  AppStatusOverlay,
  DebugButton,
  GlobalStyle,
  NoReviewQuestionsModal,
  OrganisationInfo,
  ReviewOrganisation,
  ReviewOrgSuccessModal,
  UnknownOrganisation,
  CongratulationsStories,
  variants,
  AchievementDetail,
  AchievementModal,
  PlayerYoutube,
  OnboardingQuestions,
  SignInNewsFeed,
  HereWeGoModal,
  ImpersonatingBanner,
  AchievementToast,
} from './components'
import { Provider } from 'react-redux'
import { AppStatus, Pulse, store, useAppSelector } from './redux'
import { APIClient, analytics, amplitude } from './services'
import CreateStory from './components/Modals/CreateStory'
import StoryDetails from './components/Modals/StoryDetail'
import { globalHistory, Location, useLocation } from '@reach/router'
import {
  toggleOnboardingQuestionsVisibility,
  useHandleOpen,
  useGetUTMSourcesFromURL,
  usePopulateReferralCodeFromURL,
} from './utils'
import { ModalOutlet } from '@codeleap/modals'
import { TypeGuards } from '@codeleap/types'
import { ImmersiveVideo } from './components/Player/Immersive'
import { QueryClientProvider } from '@tanstack/react-query'

amplitude.sessionReplayManager.initialize()

export const Overlays = () => (
  // This is needed for useLocation to work inside the modals
  <Location>
    {() => (
      <>
        <DebugButton />
        <OrganisationInfo />
        <ReviewOrganisation />
        <UnknownOrganisation />
        <CreateStory />
        <NoReviewQuestionsModal />
        <ReviewOrgSuccessModal />
        <StoryDetails />
        <OnboardingQuestions />
        <SignInNewsFeed />
        <CongratulationsStories />
        <AchievementDetail />
        <AchievementModal />
        <PlayerYoutube.Fullscreen />
        <HereWeGoModal />
        <AppStatusOverlay />
        <ModalOutlet />
        <ImpersonatingBanner />
        <AchievementToast />
      </>
    )}
  </Location>
)

const routesToAvoid = ['/profile/settings/']

const App = ({ children }) => {
  const session = APIClient.Session.useSession(true)

  useHandleOpen({
    mapping: {
      openQuestions: undefined,
      questions: undefined,
      openCreateStory: 'createStory',
    },
    onOpen: () => Pulse.toggleModal('start_page', true),
  })

  usePopulateReferralCodeFromURL()
  useGetUTMSourcesFromURL()

  const { pathname } = useLocation()
  const { onboardingQuestionsWasAppeared } = useAppSelector(s => s.Session)
  useIsomorphicEffect(() => {
    if (!session.loginResolved || !session.profile || onboardingQuestionsWasAppeared) return
    const storage = LocalStorage.getItem('ONBOARDING_QUESTIONS')
    const profileAnswers = session.profile?.climate_actionability && session.profile?.climate_priority

    if (TypeGuards.isNull(storage) && !profileAnswers && !routesToAvoid.includes(pathname)) {
      AppStatus.setModal(['onboardingQuestions', true])
      toggleOnboardingQuestionsVisibility()
    }
  }, [session?.profile, session.loginResolved, pathname])

  useIsomorphicEffect(() => {
    globalHistory.listen(({ action }) => {
      if (session.isLoggedIn && action === 'PUSH') {
        analytics?.track('logged_in_pageview')
      }
    })
  }, [session.isLoggedIn])

  ImmersiveVideo.useCaptureFirstInteraction()

  return (
    <>
      <GlobalStyle />
      {children}
      <Overlays />
    </>
  )
}

export const Root = ({ children }) => {
  return (
    <QueryClientProvider client={APIClient.queryClient.client}>
      <Provider store={store}>
        <StyleProvider settings={Settings} variantProvider={variantProvider} variants={variants} logger={logger}>
          <I18NProvider i18n={I18N}>
            <Location>{() => <App>{children}</App>}</Location>
          </I18NProvider>
        </StyleProvider>
      </Provider>
    </QueryClientProvider>
  )
}
